/* screens/academy/landing/blocks.jsx — реестр блоков лендинга курса (#16).
   Один компонент-рендер на тип блока {hero,text,program,features,author,reviews,
   faq,pricing,cta} согласно work/academy-feedback/contracts/landing-blocks.json.

   БЕЗОПАСНОСТЬ (scar S5/S6):
   - Рендерим ТОЛЬКО известные поля каждого типа (allowlist BLOCK_FIELDS). Любой
     неизвестный/произвольный ключ блока игнорируется — он НИКОГДА не попадёт в
     src/href. Чужой ключ не может стать атрибутом-URL.
   - Тексты (*_md / text) → ChatMarkdown (санитайз ссылок, без raw HTML).
   - URL-картинок (cover_url/photo_url/avatar_url) пропускаем через safeUrl + жёсткий
     ^https?:// — иначе картинку не рисуем (omit). javascript:/data:/относительные — вон.
   - icon (features) — только имя из реестра Icon; неизвестное имя опускаем (omit). */

import React from 'react';
import { Icon } from '../../../components/ui/Icon.jsx';
import { Button } from '../../../components/ui/Button.jsx';
import { Field, TextInput, Textarea, NativeSelect, NumberInput } from '../../../components/ui/Field.jsx';
import { ChatMarkdown } from '../../../components/patterns/chat/Markdown.jsx';
import { safeUrl } from '../../../lib/safeUrl.js';
import { submitLandingForm } from '../../../lib/api/academy.js';
import { money, homeworkModeLabel, accessDaysLabel } from '../../../lib/academy/pricing.js';

const { useState, useCallback } = React;

// Дефолтные подписи формы-заявки (CTA mode='form'). Методист может переопределить
// (block.form.submit_label / block.form.success_text); пусто — берём эти.
const FORM_SUBMIT_DEFAULT = 'Оставить заявку';
const FORM_SUCCESS_DEFAULT = 'Спасибо! Мы свяжемся с вами.';

// Известные типы блоков (allowlist). Неизвестный тип не рендерится вовсе.
const BLOCK_TYPES = ['hero', 'text', 'program', 'features', 'author', 'reviews', 'faq', 'pricing', 'cta'];

// Имена иконок, которые реально есть в реестре Icon (components/ui/Icon.jsx).
// Используется ТОЛЬКО для features[].icon: имя вне набора опускаем, чтобы не рисовать
// fallback-«!». Держим вручную (Icon.jsx не экспортирует ключи и вне scope правки).
const KNOWN_ICONS = new Set([
  'analytics', 'book', 'box', 'building', 'calendar', 'camera', 'card', 'chat', 'check',
  'circle', 'clock', 'close', 'copy', 'download', 'edit', 'eye', 'filter', 'grid', 'grip',
  'home', 'image', 'info', 'kitchen', 'layers', 'link', 'lock', 'logout', 'menu', 'minus',
  'moon', 'notifications', 'percent', 'plus', 'refresh', 'reports', 'restaurant', 'scale',
  'search', 'settings', 'sort', 'sparkles', 'spinner', 'sun', 'tag', 'trash', 'unlink',
  'upload', 'user', 'utensils', 'workspace', 'credit-card', 'crown', 'globe', 'mail',
  'microphone', 'paper-plane', 'paperclip', 'pause', 'send', 'share', 'shield', 'stop',
  'thumbs-down', 'thumbs-up', 'user-add', 'alert-circle', 'alert-triangle', 'arrow-down',
  'arrow-left', 'arrow-right', 'arrow-up', 'arrow-up-right', 'check-circle', 'chevron-down',
  'chevron-left', 'chevron-right', 'chevron-up', 'chevrons-up-down', 'external-link',
  'eye-off', 'file-text', 'more-horizontal', 'more-vertical', 'x-circle',
]);

// URL картинки для <img>. safeUrl сначала режет управляющие символы и опасные
// схемы, затем оставляем два вида адресов:
//   1) абсолютный http(s) — картинки методиста с внешнего хостинга (бэк требует
//      именно такой вид в полях блоков);
//   2) путь СВОЕГО origin, начинающийся с одного «/» — так приходит обложка
//      курса (AD-7). Раньше её отбрасывала жёсткая проверка ^https?://, и
//      загруженная методистом обложка не появлялась на странице продаж никогда.
// Форму «путь = ровно один слэш» проверяем здесь же: `//evil.com` и `/\evil.com`
// браузер читает как протокол-относительный адрес на ЧУЖОЙ хост, а не как путь.
// Первую из них режет и safeUrl, вторую — нет, и правило должно быть видно в том
// месте, где строится src.
function safeImg(url) {
  const u = safeUrl(url);
  if (!u) return null;
  if (/^https?:\/\//i.test(u)) return u;
  if (u.startsWith('/') && !u.startsWith('//') && !u.startsWith('/\\')) return u;
  return null;
}

function knownIcon(name) {
  return (typeof name === 'string' && KNOWN_ICONS.has(name)) ? name : null;
}

// Заголовок секции (общий для блоков с heading)
function BlockHeading({ children }) {
  if (!children) return null;
  return <h2 className="cl-block__heading t-h2">{children}</h2>;
}

// ── hero ───────────────────────────────────────────────────────────────────
function HeroBlock({ block, course, cta, slug, isPreview }) {
  // Обложка hero: cover_url блока (если задан и http(s)) → иначе обложка курса.
  const cover = safeImg(block.cover_url) || safeImg(course && course.cover_url);
  const title = block.title || (course && course.title) || '';
  const isForm = block.mode === 'form';
  return (
    <section className={'cl-hero' + (cover ? ' cl-hero--cover' : '')}>
      <div className="cl-hero__body">
        <div className="t-eyebrow cl-hero__eyebrow"><Icon name="sparkles" size={13} />Курс Академии</div>
        <h1 className="cl-hero__title t-display">{title}</h1>
        {block.subtitle && <p className="cl-hero__subtitle">{block.subtitle}</p>}
        {isForm
          ? <div className="cl-hero__cta"><LeadForm slug={slug} form={block.form} isPreview={isPreview} /></div>
          : (cta && <div className="cl-hero__cta">{cta(block.cta_label)}</div>)}
      </div>
      {cover && (
        <div className="cl-hero__media">
          <img src={cover} alt="" loading="lazy" />
        </div>
      )}
    </section>
  );
}

// ── text ───────────────────────────────────────────────────────────────────
function TextBlock({ block }) {
  if (!block.body_md && !block.heading) return null;
  return (
    <section className="cl-block cl-text">
      <BlockHeading>{block.heading}</BlockHeading>
      {block.body_md && <div className="ac-prose cl-text__body"><ChatMarkdown source={block.body_md} /></div>}
    </section>
  );
}

// ── program ────────────────────────────────────────────────────────────────
// mode 'auto' → строим из program (модули/уроки, приходит с бэка).
// mode 'custom' → block.items [{title,desc}].
function ProgramBlock({ block, program }) {
  const custom = block.mode === 'custom' && Array.isArray(block.items) && block.items.length;
  return (
    <section className="cl-block cl-program">
      <BlockHeading>{block.heading || 'Программа курса'}</BlockHeading>
      {custom ? (
        <ol className="cl-program__list">
          {block.items.map((it, i) => (
            <li key={i} className="cl-program__item">
              <span className="cl-program__num num">{i + 1}</span>
              <div className="cl-program__body">
                <div className="cl-program__title">{it.title}</div>
                {it.desc && <div className="cl-program__desc">{it.desc}</div>}
              </div>
            </li>
          ))}
        </ol>
      ) : (
        <ol className="cl-program__list">
          {(program || []).map((m, i) => (
            <li key={i} className="cl-program__item">
              <span className="cl-program__num num">{i + 1}</span>
              <div className="cl-program__body">
                <div className="cl-program__title">{m.module_title}</div>
                {/* Маркер списка — нейтральная точка, а не «галочка». Галочка в
                    проекте смысловая («сделано»), и на лендинге она сообщала
                    посетителю с нулевым прогрессом, что все уроки уже пройдены. */}
                {Array.isArray(m.lessons) && m.lessons.length > 0 && (
                  <ul className="cl-program__lessons">
                    {m.lessons.map((l, j) => (
                      <li key={j}><span className="cl-program__bullet" aria-hidden="true" />{l.title}</li>
                    ))}
                  </ul>
                )}
              </div>
            </li>
          ))}
          {(!program || !program.length) && (
            <li className="cl-program__empty t-caption text-mute">Программа появится позже.</li>
          )}
        </ol>
      )}
    </section>
  );
}

// ── features ───────────────────────────────────────────────────────────────
function FeaturesBlock({ block }) {
  const items = Array.isArray(block.items) ? block.items : [];
  if (!items.length) return null;
  return (
    <section className="cl-block cl-features">
      <BlockHeading>{block.heading}</BlockHeading>
      <div className="cl-features__grid">
        {items.map((it, i) => {
          const ic = knownIcon(it.icon) || 'check-circle';
          return (
            <div key={i} className="cl-feature">
              <span className="cl-feature__icon"><Icon name={ic} size={20} /></span>
              <div className="cl-feature__body">
                <div className="cl-feature__title">{it.title}</div>
                {it.desc && <div className="cl-feature__desc">{it.desc}</div>}
              </div>
            </div>
          );
        })}
      </div>
    </section>
  );
}

// ── author ─────────────────────────────────────────────────────────────────
function AuthorBlock({ block }) {
  if (!block.name) return null;
  const photo = safeImg(block.photo_url);
  return (
    <section className="cl-block cl-author">
      <BlockHeading>{block.heading || 'Автор курса'}</BlockHeading>
      <div className="cl-author__card">
        <div className="cl-author__avatar">
          {photo ? <img src={photo} alt="" loading="lazy" /> : <Icon name="user" size={28} aria-hidden="true" />}
        </div>
        <div className="cl-author__body">
          <div className="cl-author__name">{block.name}</div>
          {block.role && <div className="cl-author__role">{block.role}</div>}
          {block.bio_md && <div className="ac-prose cl-author__bio"><ChatMarkdown source={block.bio_md} /></div>}
        </div>
      </div>
    </section>
  );
}

// ── reviews ────────────────────────────────────────────────────────────────
function ReviewsBlock({ block }) {
  const items = Array.isArray(block.items) ? block.items : [];
  if (!items.length) return null;
  return (
    <section className="cl-block cl-reviews">
      <BlockHeading>{block.heading || 'Отзывы'}</BlockHeading>
      <div className="cl-reviews__grid">
        {items.map((it, i) => {
          const avatar = safeImg(it.avatar_url);
          return (
            <figure key={i} className="cl-review">
              <blockquote className="cl-review__text">{it.text}</blockquote>
              <figcaption className="cl-review__who">
                <span className="cl-review__avatar">
                  {avatar ? <img src={avatar} alt="" loading="lazy" /> : <Icon name="user" size={16} aria-hidden="true" />}
                </span>
                <span className="cl-review__meta">
                  <span className="cl-review__name">{it.name}</span>
                  {it.role && <span className="cl-review__role">{it.role}</span>}
                </span>
              </figcaption>
            </figure>
          );
        })}
      </div>
    </section>
  );
}

// ── faq ────────────────────────────────────────────────────────────────────
function FaqBlock({ block }) {
  const items = Array.isArray(block.items) ? block.items : [];
  if (!items.length) return null;
  return (
    <section className="cl-block cl-faq">
      <BlockHeading>{block.heading || 'Частые вопросы'}</BlockHeading>
      <div className="cl-faq__list">
        {items.map((it, i) => (
          <details key={i} className="cl-faq__item">
            <summary className="cl-faq__q"><span>{it.q}</span><Icon name="chevron-down" size={16} className="cl-faq__caret" /></summary>
            {it.a_md && <div className="ac-prose cl-faq__a"><ChatMarkdown source={it.a_md} /></div>}
          </details>
        ))}
      </div>
    </section>
  );
}

// ── pricing ────────────────────────────────────────────────────────────────
// auto из тарифов курса (tariffs); для бесплатного/без тарифов — короткая строка.
function PricingBlock({ block, tariffs, course, cta, onBuyTariff, buyDisabled }) {
  const list = Array.isArray(tariffs) ? tariffs : [];
  return (
    <section className="cl-block cl-pricing">
      <BlockHeading>{block.heading || 'Стоимость и тарифы'}</BlockHeading>
      {list.length > 0 ? (
        <div className="cl-pricing__grid">
          {list.map((t) => (
            <div key={t.id} className="cl-tariff">
              <div className="cl-tariff__head">
                <span className="cl-tariff__title">{t.title}</span>
                <span className="cl-tariff__price num">{money(t.price_rub)}</span>
              </div>
              <ul className="cl-tariff__feats">
                <li><Icon name="layers" size={14} aria-hidden="true" />
                  {t.includes_all_modules ? 'Все модули курса' : `${t.module_count} модуль(ей)`}</li>
                <li><Icon name="check" size={14} aria-hidden="true" />{homeworkModeLabel(t)}</li>
                <li><Icon name="clock" size={14} aria-hidden="true" />{accessDaysLabel(t)}</li>
                {t.grants_certificate
                  ? <li><Icon name="sparkles" size={14} aria-hidden="true" />Сертификат по завершении</li>
                  : null}
              </ul>
              {/* Кнопка тарифа называет ТУ ЖЕ сумму, которую спишут: главная CTA
                  при нескольких тарифах может показать только «от». */}
              {onBuyTariff && (
                <Button variant="secondary" className="cl-tariff__buy" disabled={buyDisabled}
                  onClick={() => onBuyTariff(t.id)}>Купить · {money(t.price_rub)}</Button>
              )}
            </div>
          ))}
        </div>
      ) : (
        <div className="cl-pricing__single">
          <span className="cl-pricing__amount num">
            {course && course.is_paid ? money(course.price_rub) : 'Бесплатно'}
          </span>
          {course && course.issues_certificate && (
            <span className="cl-pricing__note"><Icon name="sparkles" size={14} aria-hidden="true" />Сертификат по завершении</span>
          )}
        </div>
      )}
      {cta && <div className="cl-pricing__cta">{cta()}</div>}
    </section>
  );
}

// ── lead-форма (CTA mode='form') ─────────────────────────────────────────────
// Базовые поля: ФИО*, Телефон*, Email*, Комментарий — спрашиваются всегда (у лида
// CRM под них свои колонки). AD-14: сверх них методист собирает СВОИ поля в
// конструкторе (form.fields) — они рендерятся здесь и уезжают в том же теле
// заявки; бэк принимает только ОБЪЯВЛЕННЫЕ в лендинге ключи (anti-tamper).
// Работает БЕЗ авторизации (submitLandingForm не требует сессии). slug — из блока курса.
// На {ok:true} показываем success_text и чистим форму; 400 → ошибки по полям (DRF
// {field:[msg]}); 429 → «Слишком много запросов»; иные коды/сеть → общая ошибка.
// В preview форма инертна (submit отключён) — кнопки лендинга в превью неактивны.
const RATE_LIMITED_MSG = 'Слишком много запросов, попробуйте позже.';
const FORM_FIELD_ORDER = ['name', 'phone', 'email', 'message'];
// Типы поля формы — зеркало `_LANDING_FORM_FIELD_TYPES` бэкенда (academy/serializers.py).
// Значение вне набора не рендерим: неизвестный тип бэк на сохранении не пропустит,
// но старый/ручной landing_json мог его пронести.
const FORM_FIELD_TYPE_OPTIONS = [
  { value: 'text', label: 'Текст' },
  { value: 'phone', label: 'Телефон' },
  { value: 'email', label: 'Email' },
  { value: 'textarea', label: 'Длинный текст' },
  { value: 'select', label: 'Список' },
  { value: 'number', label: 'Число' },
];
const FORM_FIELD_TYPES = FORM_FIELD_TYPE_OPTIONS.map((o) => o.value);

// Нормализованный список ДОП. полей формы (AD-14). Базовые ключи и неизвестные
// типы отсеиваем, дубли ключей — тоже: два поля с одним key перетирали бы значение.
function extraFormFields(form) {
  const list = (form && Array.isArray(form.fields)) ? form.fields : [];
  const out = [];
  const seen = new Set();
  for (const f of list) {
    if (!f || typeof f !== 'object') continue;
    const raw = (typeof f.key === 'string' && f.key) || (typeof f.name === 'string' && f.name) || '';
    const key = raw.trim();
    if (!key || seen.has(key) || FORM_FIELD_ORDER.includes(key)) continue;
    const type = f.type || 'text';
    if (!FORM_FIELD_TYPES.includes(type)) continue;
    seen.add(key);
    out.push({
      key,
      type,
      label: (typeof f.label === 'string' && f.label.trim()) || key,
      options: Array.isArray(f.options) ? f.options.filter((o) => typeof o === 'string') : [],
    });
  }
  return out;
}

// Один ДОП. вход формы по дескриптору методиста.
function ExtraFieldInput({ desc, value, error, disabled, onChange }) {
  if (desc.type === 'textarea') {
    return <Textarea value={value} onChange={(e) => onChange(e.target.value)}
      invalid={!!error} minRows={2} maxLength={2000} disabled={disabled} />;
  }
  if (desc.type === 'select') {
    return <NativeSelect value={value} onChange={(e) => onChange(e.target.value)}
      disabled={disabled}
      options={[{ value: '', label: '— выберите —' },
        ...desc.options.map((o) => ({ value: o, label: o }))]} />;
  }
  if (desc.type === 'number') {
    return <NumberInput value={value} onChange={(v) => onChange(v == null ? '' : String(v))}
      disabled={disabled} />;
  }
  const type = desc.type === 'phone' ? 'tel' : (desc.type === 'email' ? 'email' : 'text');
  return <TextInput value={value} onChange={(e) => onChange(e.target.value)}
    invalid={!!error} type={type} maxLength={200} disabled={disabled} />;
}

function LeadForm({ slug, form, isPreview }) {
  const submitLabel = (form && typeof form.submit_label === 'string' && form.submit_label.trim())
    || FORM_SUBMIT_DEFAULT;
  const successText = (form && typeof form.success_text === 'string' && form.success_text.trim())
    || FORM_SUCCESS_DEFAULT;

  // Дескрипторы доп. полей считаем из формы один раз: список ключей нужен и для
  // сброса значений, и для раскладки ошибок 400 по полям.
  const extras = React.useMemo(() => extraFormFields(form), [form]);
  const emptyValues = React.useMemo(() => {
    const v = { name: '', phone: '', email: '', message: '' };
    for (const f of extras) v[f.key] = '';
    return v;
  }, [extras]);

  const [values, setValues] = useState(emptyValues);
  const [errors, setErrors] = useState({});       // { field: 'сообщение' } из DRF 400
  const [formError, setFormError] = useState(''); // общая ошибка (429/сеть/нет slug)
  const [done, setDone] = useState(false);
  const [busy, setBusy] = useState(false);

  const setValue = (key, val) => {
    setValues((s) => ({ ...s, [key]: val }));
    // снимаем ошибку поля при правке
    setErrors((s) => (s[key] ? { ...s, [key]: undefined } : s));
  };
  const set = (key) => (e) => setValue(key, e.target.value);

  // Клиентская валидация обязательных полей (бэк — финальный арбитр; здесь лишь UX).
  const clientValidate = useCallback(() => {
    const errs = {};
    if (!values.name.trim()) errs.name = 'Укажите ФИО.';
    if (!values.phone.trim()) errs.phone = 'Укажите телефон.';
    const email = values.email.trim();
    if (!email) errs.email = 'Укажите email.';
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) errs.email = 'Введите корректный email.';
    return errs;
  }, [values]);

  const submit = useCallback(async (e) => {
    if (e && e.preventDefault) e.preventDefault();
    if (isPreview || busy) return;
    setFormError('');
    if (!slug) { setFormError('Форма недоступна.'); return; }
    const clientErrs = clientValidate();
    if (Object.keys(clientErrs).length) { setErrors(clientErrs); return; }
    setErrors({});
    setBusy(true);
    try {
      const payload = {
        name: values.name.trim(),
        phone: values.phone.trim(),
        email: values.email.trim(),
        message: values.message.trim(),
      };
      // Доп. поля кладём в то же тело; пустые не отправляем (они необязательны).
      for (const f of extras) {
        const v = values[f.key];
        const s = typeof v === 'string' ? v.trim() : v;
        if (s !== '' && s != null) payload[f.key] = s;
      }
      const { ok, status, data } = await submitLandingForm(slug, payload);
      if (ok && data && data.ok) {
        setDone(true);
        setValues(emptyValues);
      } else if (status === 429) {
        setFormError(RATE_LIMITED_MSG);
      } else if (status === 400 && data && typeof data === 'object') {
        // DRF field errors {field:[msg]} → раскладываем по полям; нераспознанное → общая.
        const fieldErrs = {};
        let other = '';
        const known = new Set([...FORM_FIELD_ORDER, ...extras.map((f) => f.key)]);
        for (const [k, v] of Object.entries(data)) {
          const msg = Array.isArray(v) ? v[0] : (typeof v === 'string' ? v : '');
          if (!msg) continue;
          if (known.has(k)) fieldErrs[k] = msg;
          else other = other || msg; // non_field_errors / detail и пр.
        }
        if (Object.keys(fieldErrs).length) setErrors(fieldErrs);
        if (!Object.keys(fieldErrs).length || other) {
          setFormError(other || 'Проверьте правильность заполнения полей.');
        }
      } else if (status === 404) {
        setFormError('Курс не найден.');
      } else {
        setFormError((data && (data.detail || data.error)) || 'Не удалось отправить заявку.');
      }
    } catch {
      setFormError('Не удалось отправить заявку. Попробуйте позже.');
    } finally {
      setBusy(false);
    }
  }, [isPreview, busy, slug, clientValidate, values, extras, emptyValues]);

  if (done) {
    return (
      <div className="cl-leadform cl-leadform--done" role="status">
        <Icon name="check-circle" size={20} aria-hidden="true" />
        <span className="cl-leadform__success">{successText}</span>
      </div>
    );
  }

  return (
    <form className="cl-leadform" onSubmit={submit} noValidate>
      <Field label="ФИО" required error={errors.name}>
        <TextInput value={values.name} onChange={set('name')} invalid={!!errors.name}
          autoComplete="name" maxLength={200} disabled={busy} />
      </Field>
      <Field label="Телефон" required error={errors.phone}>
        <TextInput value={values.phone} onChange={set('phone')} invalid={!!errors.phone}
          type="tel" autoComplete="tel" maxLength={40} disabled={busy} />
      </Field>
      <Field label="Email" required error={errors.email}>
        <TextInput value={values.email} onChange={set('email')} invalid={!!errors.email}
          type="email" autoComplete="email" maxLength={254} disabled={busy} />
      </Field>
      {/* AD-14: поля, добавленные методистом в конструкторе формы. */}
      {extras.map((f) => (
        <Field key={f.key} label={f.label} error={errors[f.key]}>
          <ExtraFieldInput desc={f} value={values[f.key] ?? ''} error={errors[f.key]}
            disabled={busy} onChange={(v) => setValue(f.key, v)} />
        </Field>
      ))}
      <Field label="Комментарий" error={errors.message}>
        <Textarea value={values.message} onChange={set('message')} invalid={!!errors.message}
          minRows={2} maxLength={2000} disabled={busy} />
      </Field>
      {formError && (
        <div className="cl-leadform__error" role="alert">
          <Icon name="alert-circle" size={14} aria-hidden="true" />
          <span>{formError}</span>
        </div>
      )}
      <Button type="submit" variant="primary" size="lg" loading={busy} disabled={isPreview}>
        {submitLabel}
      </Button>
    </form>
  );
}

// ── cta ────────────────────────────────────────────────────────────────────
// mode='form' → форма-заявки (LeadForm). Иначе (mode='enroll'/пусто) — обычная
// CTA-кнопка через render-prop ctx.cta (поведение записаться/купить не меняется).
function CtaBlock({ block, cta, slug, isPreview }) {
  const isForm = block.mode === 'form';
  return (
    <section className="cl-block cl-cta">
      {/* Форма ставится ПОД текст, кнопка остаётся сбоку. Рядом с текстом форма
          из четырёх полей ломала блок на две узкие колонки: заголовок жался
          слева, поля справа, и читать приходилось зигзагом. Кнопке место сбоку
          идёт — она короткая и дочитывается как завершение фразы. */}
      <div className={'cl-cta__card' + (isForm ? ' cl-cta__card--form' : '')}>
        <div className="cl-cta__text">
          <div className="cl-cta__title t-h2">{block.title}</div>
          {block.subtitle && <div className="cl-cta__subtitle">{block.subtitle}</div>}
        </div>
        {isForm
          ? <div className="cl-cta__action"><LeadForm slug={slug} form={block.form} isPreview={isPreview} /></div>
          : (cta && <div className="cl-cta__action">{cta(block.cta_label)}</div>)}
      </div>
    </section>
  );
}

// Реестр: тип → компонент. ctx = { course, program, tariffs, cta, slug, isPreview }.
// slug/isPreview нужны блокам с формой-заявкой (hero/cta при mode='form').
const REGISTRY = {
  hero: (block, ctx) => <HeroBlock block={block} course={ctx.course} cta={ctx.cta} slug={ctx.slug} isPreview={ctx.isPreview} />,
  text: (block) => <TextBlock block={block} />,
  program: (block, ctx) => <ProgramBlock block={block} program={ctx.program} />,
  features: (block) => <FeaturesBlock block={block} />,
  author: (block) => <AuthorBlock block={block} />,
  reviews: (block) => <ReviewsBlock block={block} />,
  faq: (block) => <FaqBlock block={block} />,
  pricing: (block, ctx) => <PricingBlock block={block} tariffs={ctx.tariffs} course={ctx.course}
    cta={ctx.cta} onBuyTariff={ctx.onBuyTariff} buyDisabled={ctx.isPreview} />,
  cta: (block, ctx) => <CtaBlock block={block} cta={ctx.cta} slug={ctx.slug} isPreview={ctx.isPreview} />,
};

// Рендер одного блока по типу из allowlist. Неизвестный тип → ничего.
function renderBlock(block, ctx) {
  if (!block || typeof block !== 'object') return null;
  if (!BLOCK_TYPES.includes(block.type)) return null;
  const render = REGISTRY[block.type];
  return render ? render(block, ctx) : null;
}

/* ── BLOCK_SCHEMA — дескриптор полей каждого типа (#16) ─────────────────────────
   Единый источник правды для ФОРМ редактора (LandingEditor.jsx) и для дефолтов нового
   блока. Точно повторяет work/academy-feedback/contracts/landing-blocks.json, чтобы
   сохранённый landing_json проходил бэк-валидацию (S13: контракт фронт↔бэк не дрейфует).
   Рендер выше читает ровно те же поля — расхождения быть не может.

   Дескриптор поля:
     key   — имя поля в объекте блока
     label — подпись в форме
     kind  — 'text' | 'textarea' (markdown) | 'image' | 'select' | 'items'
     hint? — подсказка
     options? (select) — [{value,label}]
     item? (items)     — { fields:[…дескрипторы скаляров…] } для строк массива items[]
   Поля-картинки ('image') — это URL с загрузчиком файла рядом: методисту больше не
   нужен внешний хостинг. Вид адреса бэк по-прежнему требует ^https?:// (S5/S6),
   и загрузчик отдаёт именно абсолютный адрес нашего публичного контура. */
const IMG_HINT = 'Загрузите файл или вставьте ссылку http(s)://';
const MD_HINT = 'Поддерживается Markdown';

const BLOCK_SCHEMA = {
  hero: {
    label: 'Шапка (hero)',
    fields: [
      { key: 'title', label: 'Заголовок', kind: 'text' },
      { key: 'subtitle', label: 'Подзаголовок', kind: 'text' },
      { key: 'cover_url', label: 'Обложка', kind: 'image', hint: 'пусто — берётся обложка курса · ' + IMG_HINT },
      { key: 'cta_label', label: 'Текст кнопки', kind: 'text', hint: 'пусто — текст по умолчанию' },
    ],
  },
  text: {
    label: 'Текстовый блок',
    fields: [
      { key: 'heading', label: 'Заголовок', kind: 'text' },
      { key: 'body_md', label: 'Текст', kind: 'textarea', hint: MD_HINT },
    ],
  },
  program: {
    label: 'Программа',
    fields: [
      { key: 'heading', label: 'Заголовок', kind: 'text', hint: 'пусто — «Программа курса»' },
      { key: 'mode', label: 'Источник', kind: 'select', hint: 'auto — из модулей курса · своя — пункты ниже',
        options: [{ value: 'auto', label: 'Из модулей курса (авто)' }, { value: 'custom', label: 'Свой список' }] },
      { key: 'items', label: 'Пункты программы', kind: 'items', showWhen: (b) => b.mode === 'custom',
        item: { fields: [
          { key: 'title', label: 'Название', kind: 'text' },
          { key: 'desc', label: 'Описание', kind: 'text' },
        ] } },
    ],
  },
  features: {
    label: 'Преимущества',
    fields: [
      { key: 'heading', label: 'Заголовок', kind: 'text' },
      { key: 'items', label: 'Пункты', kind: 'items',
        item: { fields: [
          { key: 'title', label: 'Название', kind: 'text' },
          { key: 'desc', label: 'Описание', kind: 'text' },
          { key: 'icon', label: 'Иконка', kind: 'text', hint: 'имя из реестра иконок (напр. check-circle); неизвестное — заменится' },
        ] } },
    ],
  },
  author: {
    label: 'Автор',
    fields: [
      { key: 'heading', label: 'Заголовок', kind: 'text', hint: 'пусто — «Автор курса»' },
      { key: 'name', label: 'Имя', kind: 'text' },
      { key: 'role', label: 'Должность / регалии', kind: 'text' },
      { key: 'photo_url', label: 'Фото', kind: 'image', hint: IMG_HINT },
      { key: 'bio_md', label: 'О себе', kind: 'textarea', hint: MD_HINT },
    ],
  },
  reviews: {
    label: 'Отзывы',
    fields: [
      { key: 'heading', label: 'Заголовок', kind: 'text', hint: 'пусто — «Отзывы»' },
      { key: 'items', label: 'Отзывы', kind: 'items',
        item: { fields: [
          { key: 'name', label: 'Имя', kind: 'text' },
          { key: 'role', label: 'Должность', kind: 'text' },
          { key: 'text', label: 'Текст отзыва', kind: 'textarea' },
          { key: 'avatar_url', label: 'Фото', kind: 'image', hint: IMG_HINT },
        ] } },
    ],
  },
  faq: {
    label: 'Вопросы и ответы',
    fields: [
      { key: 'heading', label: 'Заголовок', kind: 'text', hint: 'пусто — «Частые вопросы»' },
      { key: 'items', label: 'Вопросы', kind: 'items',
        item: { fields: [
          { key: 'q', label: 'Вопрос', kind: 'text' },
          { key: 'a_md', label: 'Ответ', kind: 'textarea', hint: MD_HINT },
        ] } },
    ],
  },
  pricing: {
    label: 'Тарифы и цена',
    fields: [
      { key: 'heading', label: 'Заголовок', kind: 'text', hint: 'пусто — «Стоимость и тарифы» · карточки строятся из тарифов курса' },
    ],
  },
  cta: {
    label: 'Призыв к действию',
    fields: [
      { key: 'title', label: 'Заголовок', kind: 'text' },
      { key: 'subtitle', label: 'Подзаголовок', kind: 'text' },
      // «либо-либо»: enroll (просто записаться/купить) — дефолт; form (форма заявки → CRM).
      { key: 'mode', label: 'Действие', kind: 'select',
        hint: 'Запись/покупка — кнопка ведёт на курс · Форма заявки — посетитель оставляет контакты (попадут в CRM)',
        options: [
          { value: 'enroll', label: 'Запись / покупка (кнопка)' },
          { value: 'form', label: 'Форма заявки' },
        ] },
      // Текст кнопки нужен только в режиме «запись/покупка».
      { key: 'cta_label', label: 'Текст кнопки', kind: 'text', showWhen: (b) => (b.mode || 'enroll') !== 'form' },
      // Подписи формы — только в режиме «форма заявки». ФИО/телефон/email/комментарий
      // спрашиваются всегда; всё сверх них методист собирает сам (AD-14).
      { key: 'form.submit_label', label: 'Текст кнопки отправки',
        kind: 'text', hint: 'пусто — «' + FORM_SUBMIT_DEFAULT + '»', showWhen: (b) => b.mode === 'form' },
      { key: 'form.success_text', label: 'Текст после отправки',
        kind: 'text', hint: 'пусто — «' + FORM_SUCCESS_DEFAULT + '»', showWhen: (b) => b.mode === 'form' },
      // AD-14: конструктор доп. полей формы + воронка CRM, куда уедет заявка.
      { key: 'form.fields', label: 'Дополнительные поля формы', kind: 'formfields',
        hint: 'ФИО, телефон, email и комментарий спрашиваются всегда — добавьте свои',
        showWhen: (b) => b.mode === 'form' },
      { key: 'form.pipeline', label: 'Воронка CRM', kind: 'pipeline',
        hint: 'куда попадёт заявка; пусто — воронка по умолчанию',
        showWhen: (b) => b.mode === 'form' },
    ],
  },
};

// Подпись типа блока (для списка в редакторе). Неизвестный тип → сам идентификатор.
function blockTypeLabel(type) {
  return (BLOCK_SCHEMA[type] && BLOCK_SCHEMA[type].label) || type;
}

// Пустой блок выбранного типа с дефолтами (для «Добавить блок»). id назначает вызывающий.
function emptyBlock(type) {
  const b = { type };
  if (type === 'program') b.mode = 'auto';
  if (type === 'cta') b.mode = 'enroll'; // «либо-либо»: дефолт — запись/покупка (не форма)
  return b;
}

export { renderBlock, BLOCK_TYPES, BLOCK_SCHEMA, blockTypeLabel, emptyBlock, safeImg, knownIcon,
  FORM_FIELD_TYPE_OPTIONS, FORM_FIELD_ORDER, extraFormFields };
